import {
  Button,
  Field,
  Icon,
  Input,
  pxToRem,
  Select,
  useArrayState,
  useUpdateEffect,
} from '@lbcamden/lib-react';
import { FunctionComponent } from 'react';
import { z } from 'zod';

import { actionTypeOptions } from '~/common/config/pcfra/recommendations';
import { pcfraRecommendationSchema } from '~/common/schemas/pcfra';
import { PcfraRecommendation } from '~/common/types/pcfra';

import {
  Divider,
  RecommendationContainer,
  RecommendationListRoot,
} from './RecommendationList.styled';

type Props = {
  initialValue?: Partial<PcfraRecommendation>[];
  errorDetails?: z.core.$ZodIssue[];
  onChange(value: Partial<PcfraRecommendation>[]): void;
};

export const RecommendationList: FunctionComponent<Props> = ({
  initialValue,
  errorDetails,
  onChange,
}) => {
  const { items, addItem, removeItem, updateItem, setItems } = useArrayState(initialValue);

  const getErrorMessage = (index: number, field: keyof PcfraRecommendation) =>
    errorDetails?.find(({ path }) => path.at(-2) === index && path.at(-1) === field)?.message;

  useUpdateEffect(() => {
    onChange(items);
  }, [items]);

  useUpdateEffect(() => {
    setItems(initialValue || []);
  }, [initialValue]);

  return (
    <RecommendationListRoot>
      {items.map(({ type, priority, notes }, index) => {
        const typeError = getErrorMessage(index, 'type');
        const priorityError = getErrorMessage(index, 'priority');
        const notesError = getErrorMessage(index, 'notes');

        return [
          <RecommendationContainer data-testid={`action-${index}`} key={`item-${index}`}>
            <Field label="Action type *" labelFor={`type-${index}`} errorMessage={typeError}>
              <Select
                $error={!!typeError}
                id={`type-${index}`}
                placeholder="Please select"
                options={actionTypeOptions}
                value={type}
                onChange={(value) => updateItem(index, { type: value })}
              />
            </Field>
            <Field label="Priority *" labelFor={`priority-${index}`} errorMessage={priorityError}>
              <Select
                $error={!!priorityError}
                id={`priority-${index}`}
                placeholder="Please select"
                options={pcfraRecommendationSchema.shape.priority.options}
                value={priority}
                onChange={(value) => updateItem(index, { priority: value })}
              />
            </Field>
            <Field label="Notes *" labelFor={`notes-${index}`} errorMessage={notesError}>
              <Input
                $error={!!notesError}
                id={`notes-${index}`}
                value={notes}
                onChange={(e) => updateItem(index, { notes: e.target.value })}
              />
            </Field>
            <Button $variant="tertiary" $color="primary" onClick={() => removeItem(index)}>
              <Icon icon="ui/bin-solid" width={pxToRem(14)} />
              Remove action
            </Button>
          </RecommendationContainer>,
          <Divider key={`divider-${index}`} />,
        ];
      })}
      <Button
        css={{ alignSelf: 'flex-start' }}
        $variant="tertiary"
        $color="primary"
        onClick={() => addItem({ notes: '' })}
      >
        <Icon icon="ui/plus" width={pxToRem(12)} />
        Add new action
      </Button>
    </RecommendationListRoot>
  );
};
